import { timerHelperModule } from '../../helpers/timer-helper';
import { configServiceModule } from '../../services/config-service/config-service';
import runnerFrameTemplate from './runner-frame.tpl.html';
import runnerFrameContactCandidateTemplate from './runner-frame-contact-candidate.tpl.html';
import runnerFrameContactTeamTemplate from './runner-frame-contact-team.tpl.html';
import './runner-frame.less';
import { appAlertComponentModule } from '../app-alert/app-alert';
import { windowEventHelperModule } from '../../helpers/window-event-helper/window-event-helper';
import { remove } from '../../utils/array-utils';
import { appConfig } from '../../constants/app-config';
import { compileTemplateComponentModule } from '../compile-template/compile-template';
import { connectToChild } from '../../vendor/penpal';

const MAX_BUFFER_SIZE = 1024 * 1024; // limit total output to 1Mb

const runnerFrames = [];
let runnerIFrame = null;

// v2 of the runner frame protocol: Penpal messaging with token authorization
const RUNNER_API_VERSION = 'v2';

const crThemeMap = {
  light: 'day',
  dark: 'night',
};

const messageIdPrefix = 'Q' + Math.random().toString(16).substr(2) + '-';
let messageIdCounter = 0;

// shared runnerUrl so we don't have to keep looking it up
let runnerUrl = null;
let didReportRunnerError;

export const runnerFrameComponentModule = angular
  .module('Q.components.runner-frame', [
    timerHelperModule,
    configServiceModule,
    appAlertComponentModule,
    windowEventHelperModule,
    compileTemplateComponentModule,
  ])

  .component('runnerFrame', {
    bindings: {
      utmSource: '<',
      utmCampaign: '<',
      utmMedium: '<',
      response: '<',
      initialResponse: '<',
      privileged: '<',
      onFocus: '&',
      onReady: '&',
    },
    template: runnerFrameTemplate,
    controller: function (
      $sce,
      runnerFrameService,
      $scope,
      $document,
      $timeout,
      $element,
      $injector,
      timerHelper,
      $log,
      $http,
    ) {
      const ctrl = this;

      ctrl.contactTemplate = appConfig.candidateApp
        ? runnerFrameContactCandidateTemplate
        : runnerFrameContactTeamTemplate;

      // create a stack of runner frames to automatically reload the frame when we return
      runnerFrames.unshift(ctrl);

      $scope.$on(
        'document:focusoutActiveElement',
        ($e, event, activeElement) => {
          if (runnerIFrame && activeElement === runnerIFrame[0]) {
            ctrl.onFocus();
          }
        },
      );

      const cmOpts =
        $injector.has('codemirrorOptions') &&
        $injector.get('codemirrorOptions').sharedOpts;

      function setUrl() {
        // if we haven't got our URL yet, ignore this request
        if (!runnerUrl) return;

        const queryParams = {
          utm_source: ctrl.utmSource || 'qualified',
          utm_medium: ctrl.utmMedium || 'web',
          utm_campaign: ctrl.utmCampaign,
          api: RUNNER_API_VERSION,
        };
        const hashParams = {
          theme: 'light',
          pro: true,
          detectHTML: false,
        };
        if (cmOpts && cmOpts.theme === 'dark') {
          hashParams.theme = 'dark';
        }
        if (ctrl.privileged) {
          hashParams.privileged = true;
        }
        if (ctrl.response) {
          hashParams.displayOnly = true;
        }

        const runnerSrc = `${runnerUrl}?${parameterize(queryParams)}&#${parameterize(hashParams)}`;
        const oldSrc = runnerIFrame.attr('src');
        if (oldSrc !== runnerSrc) {
          const isLoaded = oldSrc && oldSrc !== 'about:blank';
          runnerIFrame.attr('src', $sce.trustAsResourceUrl(runnerSrc));
          // only the hash changed: the frame won't reload, so just update the theme
          if (isLoaded && oldSrc.split('#')[0] === runnerSrc.split('#')[0]) {
            runnerFrameService.setTheme(crThemeMap[hashParams.theme]);
          } else {
            // Penpal must be listening before the frame loads
            runnerFrameService.connect(runnerIFrame[0]);
          }
        }
      }

      function clearLoadingAlert(success) {
        ctrl.loading = false;
        if (runnerIFrame) {
          runnerIFrame.css({ opacity: 1 });

          // if we're not successful loading the runner, or the iFrame itself is marked as hidden
          // (due to adblockers), we need to let the user know
          if (
            ctrl.loaded ||
            (success && runnerIFrame.isVisible(/* don't recurse */ false))
          ) {
            ctrl.loadError = false;
            ctrl.loaded = true;
          } else {
            // there was an error loading the runner frame
            let message =
              '<p>Please try reloading the page to continue (your work will be saved).</p>';

            if (runnerUrl && !didReportRunnerError) {
              didReportRunnerError = true;
              const errorType = success
                ? 'iframe was hidden externally'
                : 'content did not load within 20 seconds';
              $log.error(`Unable to load runner frame: ${errorType}.`);
              // attempt to load the CR directly, to see if we get an error
              $http
                .get(runnerIFrame.attr('src'), { autoRetry: false })
                .catch((response) => {
                  // if we didn't get a network error, report what we can
                  if (response.status > 0) {
                    $log.error(
                      `Error details when attempting to load runner frame: ${response.status} - ${response.statusText}`,
                      response,
                    );
                  }
                });

              ctrl.allowReload = true;
              message = `\
<p>Please verify that nothing is preventing access to <a class="alert-link" href="${runnerUrl}" target="_blank">${runnerUrl}</a>. Make sure to check any ad-blockers or firewall rules.</p>\
<p>If you are able to connect to this URL, please refresh this page to try again (your work will be saved).</p>\
`;
            }

            ctrl.loadError = $sce.trustAsHtml(message);
          }
        }
      }

      if (!runnerIFrame) {
        // create the shared runner iframe element
        runnerIFrame = angular.element(
          '<iframe id="runner-frame" sandbox="allow-scripts allow-same-origin allow-pointer-lock"></iframe>',
        );
      }

      // loads the runner & handles notification details
      function loadRunner(force) {
        ctrl.loading = true;
        ctrl.loadWarning = false;
        ctrl.loadError = false;
        ctrl.loaded = false;

        const timeouts = [
          // wait at least 2s before showing the loading message
          timerHelper.longTimeout(() => {
            ctrl.loading = ctrl.loading && 'show';
          }, 2000),
          // in case the iFrame never loads correctly, we'll clear the alert so it can be seen by the user
          timerHelper.longTimeout(() => {
            ctrl.loadWarning = true;
          }, 10000),
          // after 20 seconds, switch to an error message (it might still be cleared later)
          timerHelper.longTimeout(() => {
            clearLoadingAlert(false);
          }, 20000),
        ];
        $scope.$on('$destroy', () => {
          // if we're destroyed _before_ loading, don't show errors!
          timeouts.forEach(timerHelper.longTimeout.cancel);
        });

        runnerIFrame.css({ opacity: 0 });
        // must be ready to receive `onReady` before the frame is connected
        runnerFrameService.init();
        if (!runnerUrl) {
          runnerFrameService
            .getRunnerUrl()
            .then((url) => {
              runnerUrl = url;
              $element.append(runnerIFrame);
              setUrl();
            })
            .catch(() => clearLoadingAlert(false));
        } else {
          $element.append(runnerIFrame);
          setUrl();
        }

        if (cmOpts) {
          $scope.$watch(() => cmOpts.theme, setUrl);
        }

        runnerFrameService.ready().then(() => {
          // once the runner has loaded, clear our loading alert
          clearLoadingAlert(true);
          if (ctrl.response) {
            if (!ctrl.outputWatcher) {
              ctrl.outputWatcher = $scope.$watch('$ctrl.response', () => {
                displayResponse(ctrl.response);
              });
            } else if (force) {
              displayResponse(ctrl.response);
            }
          } else if (ctrl.initialResponse && !ctrl.initialResponse.$mock) {
            displayResponse(ctrl.initialResponse);
          }

          ctrl.onReady({
            setResponse(response) {
              ctrl.response = response;
              displayResponse(response);
            },
          });
        });
      }

      ctrl.$onInit = loadRunner;

      ctrl.reload = (force) => {
        if (runnerIFrame) {
          // force reloading of the iframe
          runnerIFrame.attr('src', 'about:blank');
        }
        loadRunner(force);
      };

      $scope.$on('$destroy', () => {
        if (runnerIFrame.parent()[0] === $element[0]) {
          // save this for later if it's not already been moved
          runnerIFrame.attr('src', 'about:blank');
          runnerIFrame.detach();
          // make sure we clear any waiting processes
          runnerFrameService.reset();
        }

        // if we're the top frame, try to reload the next frame in a tick (allows for multiple frames
        // to get removed if we're shutting down.
        if (
          runnerFrames[0] === ctrl &&
          runnerFrames.length > 1 &&
          !runnerFrames.reloadTimeout
        ) {
          runnerFrames.reloadTimeout = $timeout(() => {
            if (runnerFrames.length) {
              runnerFrames[0].reload(true);
            }
            runnerFrames.reloadTimeout = null;
          });
        }
        // remove this frame from anywhere in the set
        remove(runnerFrames, ctrl);
      });

      function displayResponse(response) {
        response = angular.extend(
          {
            // stdout needs to be truthy so CR doesnt show a missing output message
            // TODO: bit of a hack so lets revisit this after the CR UI switch to vue -> entrefuse/coderunner#44
            stdout: ' ',
            stderr: '',
            completed:
              response.passed > 0 && response.failed === 0 && !response.stderr,
            output: [],
          },
          response,
        );
        runnerFrameService.display({ response });
      }

      function parameterize(obj) {
        const params = [];
        angular.forEach(obj, (value, key) => {
          if (value !== undefined) {
            params.push(`${key}=${value}`);
          }
        });
        return params.join('&');
      }
    },
  })

  .factory(
    'runnerFrameService',
    function ($q, $log, $http, configService, timerHelper, $sce, appAlert) {
      let readyPromise;
      let connection = null;
      let childPromise = null;
      const configPromise = configService.load();
      const postDeferreds = {};
      // set by the embed app: anonymous visitors get a runner token from the embed endpoint
      let embedAuth = null;

      // Methods the runner frame (v2) calls on us through Penpal
      const parentMethods = {
        // the frame calls this for a token and again when the current one is close to expiring
        authorize: () =>
          (embedAuth
            ? $http.post(
                `${appConfig.baseApiRoot}service_auth/embed`,
                embedAuth,
              )
            : $http.post(`${appConfig.baseApiRoot}service_auth`, {
                service: 'runner',
              })
          ).then(
            (resp) => resp.data.token,
            // Penpal can't clone an $http response, so reject with an Error
            (resp) => {
              throw new Error(
                `service_auth failed (HTTP ${resp && resp.status})`,
              );
            },
          ),
        onReady: () => {
          if (readyPromise) readyPromise.resolve();
        },
        onReceipt: () => {},
      };

      return {
        init,

        /**
         * Authorize runs through the anonymous embed endpoint instead of a user session
         * @param auth {{embedClientKey: String, challengeId: String}}
         */
        setEmbedAuth(auth) {
          embedAuth = auth;
        },

        /**
         * posts a message to our remote code runner through an iframe
         * @param data {object} should be an object with the following values:
         * @param data.ciphered {Array<String>} Array of fields which are encrypted
         * @param data.code {String} solution code
         * @param data.files {Array<{path,contents,encrypted}>} Array of file contents
         * @param data.fixture {String} test cases
         * @param data.language {String} language to run in
         * @param data.languageVersion {String} language version
         * @param data.privilegeMode {String} 'none' or 'full' depending on what to show the candidate
         * @param data.relayId {String}
         * @param data.services {Array<String>} List of services to enable
         * @param data.setup {String} Setup code
         * @param data.successMode {String}
         * @param data.testFramework {String} Testing framework to run
         * @param data.maxBufferSize {Number} override the max buffer size set internally
         * @returns {Promise} Deferred Promise for a the response
         */
        run(data) {
          if (!data || !(data.code || (data.files && data.files.length))) {
            appAlert.warning(
              'Nothing to run! Please add some code and try again.',
            );
            return $q.reject();
          }
          data = Object.assign(
            {
              maxBufferSize: MAX_BUFFER_SIZE,
            },
            data,
          );
          return post('run', data, true).catch((e) => {
            if (!(e && e.canceled)) {
              if (e && !e.runnerFrameReset && !e.timeout) {
                $log.error(e);
              }
              appAlert.warning(
                `There was an error running your code. Please try again.${e && e.timeout ? ' (' + e.msg + ')' : ''}`,
              );
            }
            return $q.reject(e);
          });
        },
        reset,
        connect,
        setTheme,
        display: (data) => post('display', data),
        ready: () => (readyPromise ? readyPromise.promise : $q.reject()),
        getRunnerUrl,
      };

      function init() {
        readyPromise = $q.defer();
      }

      /**
       * Connects to the runner frame. Must be called after the frame's src is set, and before it loads.
       * @param iframe {HTMLIFrameElement}
       * @param connectFn {Function} Penpal's connectToChild, overridable for tests
       */
      function connect(iframe, connectFn = connectToChild) {
        disconnect();
        // No handshake timeout: Penpal destroys the connection when it fires, so a slow
        // frame could never connect. The loading alert already covers the 20s error.
        const conn = connectFn({ iframe, methods: parentMethods });
        connection = conn;
        childPromise = conn.promise;
        // destroying a connection rejects its promise, which is expected
        childPromise.catch(() => {});
      }

      function disconnect() {
        if (connection) {
          try {
            connection.destroy();
          } catch (err) {
            /* ignore errors */
          }
        }
        connection = null;
        childPromise = null;
      }

      function setTheme(theme) {
        if (!childPromise) return $q.resolve();
        return $q
          .when(childPromise.then((child) => child.setOptions({ theme })))
          .catch((e) => $log.warn(e));
      }

      function post(method, data, showRunTimeout) {
        const postDeferred = $q.defer();
        const messageId = messageIdPrefix + ++messageIdCounter;
        postDeferreds[messageId] = postDeferred;
        let runTimeout = null;
        // cleared once the request settles (canceled, timed out or reset), so a frame that
        // connects late doesn't start a run nobody is waiting for
        let active = true;

        if (!childPromise) {
          rejectPostPromise(messageId, new Error('[RunnerFrame] No iFrame'));
        } else {
          $q.when(childPromise.then((child) => active && child[method](data)))
            .then((response) => {
              if (response && response.error) {
                rejectPostPromise(messageId, response.error);
              } else {
                resolvePostPromise(messageId, response && response.result);
              }
            })
            .catch((err) => {
              $log.error(err);
              rejectPostPromise(messageId, err);
            });

          if (showRunTimeout) {
            // if we dont' get a response within 15s, give the user a chance to cancel the run
            runTimeout = timerHelper.longTimeout(() => {
              const msg = $sce.trustAsHtml(`\
<span class="loading-spinner"></span> <strong>Waiting on Results...</strong> If you are experiencing issues running your code, you may cancel the run.\
`);

              appAlert.info(msg, {
                showFor: 40000,
                linkTitle: 'Cancel Run',
                linkAction: () => {
                  appAlert.clear();
                  postDeferred.reject({ canceled: true });
                },
              });
              // after another 45s (60 total), we'll re-enable the buttons so the UI doesn't lock up
              runTimeout = timerHelper.longTimeout(() => {
                appAlert.clear();
                postDeferred.reject({
                  timeout: true,
                  msg: 'No response in 60 seconds',
                });
              }, 45000);
            }, 15000);
          }
        }
        postDeferred.promise.finally(() => {
          active = false;
          timerHelper.longTimeout.cancel(runTimeout);
        });
        return postDeferred.promise;
      }

      /**
       * Resets any running requests.
       */
      function reset() {
        const err = new Error('[RunnerFrame] Runner Frame Reset');
        err.runnerFrameReset = true;
        rejectPostPromise(null, err);
      }

      function getRunnerUrl() {
        return configPromise.then((cfg) => cfg.runnerClientUrl);
      }

      /**
       *
       * @param responseMessage {*} If string, the key for the deferred, otherwise try to look up the
       * messageId based on the passed in object
       * @param remove {boolean} if true, also delete the postDeferred at the same time
       * @returns {Deferred?}
       */
      function getPostDeferred(responseMessage, remove = false) {
        let messageId = null;
        if (angular.isString(responseMessage)) {
          messageId = responseMessage;
        } else if (responseMessage) {
          if (responseMessage.messageId) {
            messageId = responseMessage.messageId;
          } else if (responseMessage.message) {
            messageId = responseMessage.message.messageId;
          }
        }
        if (messageId) {
          const postDeferred = postDeferreds[messageId];
          if (remove) delete postDeferreds[messageId];
          return postDeferred;
        }
      }

      function resolvePostPromise(responseMessage, ...args) {
        const postDeferred = getPostDeferred(responseMessage, true);
        if (postDeferred) {
          postDeferred.resolve(...args);
        }
      }

      function rejectPostPromise(responseMessage, ...args) {
        if (!responseMessage) {
          Object.keys(postDeferreds).forEach((k) =>
            rejectPostPromise(k, ...args),
          );
        } else {
          const postDeferred = getPostDeferred(responseMessage, true);
          if (postDeferred) {
            postDeferred.reject(...args);
          }
        }
      }
    },
  );
